/**
 * Package Expansion - Loads package members (procedures/functions) when a package node is expanded
 */

import {
  DatabaseExplorerNode,
  DatabaseObjectTreeNode,
  isDatabaseObjectNode,
} from '../../explorer/models/DatabaseTreeModels';

export interface LoadPackageMembersOptions {
  node: DatabaseObjectTreeNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
}

/**
 * Loads the members (procedures/functions) for a package node when expanded.
 * Calls the Oracle package details IPC to fetch members.
 */
export async function loadPackageMembers({
  node,
  updateNode,
}: LoadPackageMembersOptions): Promise<void> {
  // Don't reload if already has children
  if (node.children && node.children.length > 0) {
    return;
  }

  if (!isDatabaseObjectNode(node)) return;

  const { objectDetails } = node.metadata;
  const connectionId = objectDetails.connectionId;
  const schema = objectDetails.schema;
  const packageName = objectDetails.name;

  if (!connectionId || !schema || !packageName) return;

  // Show loading state
  updateNode(node.id, { isLoading: true });

  try {
    const result = await window.electronAPI!.oraclePackage.getDetails({
      connectionId,
      schema,
      packageName,
    });

    if (!result.success || !result.members || result.members.length === 0) {
      updateNode(node.id, {
        isLoading: false,
        children: [],
      });
      return;
    }

    // Create child nodes for each member
    const children: DatabaseExplorerNode[] = result.members.map((member) => ({
      id: `${node.id}::member::${member.name}${member.overloadIndex ? `::${member.overloadIndex}` : ''}`,
      parentId: node.id,
      name: member.overloadIndex ? `${member.name} (overload ${member.overloadIndex})` : member.name,
      type: member.type === 'function' ? 'function' : 'stored-procedure',
      children: [],
      isExpanded: false,
      canHaveChildren: false,
      metadata: {
        objectDetails: {
          ...objectDetails,
          name: member.name,
          type: member.type === 'function' ? 'function' : 'stored-procedure',
          metadata: {
            ...objectDetails.metadata,
            packageName,
            overloadIndex: member.overloadIndex,
          },
        },
      },
    }));

    updateNode(node.id, {
      isLoading: false,
      children,
      isExpanded: true,
    });
  } catch (error) {
    console.error('[PackageExpansion] Error loading package members:', error);
    updateNode(node.id, {
      isLoading: false,
      children: [],
    });
  }
}
